import { useCallback, useEffect, useState } from 'react';
import type { ManagementSource, ManagementViewMode } from '@/components/management';
import { MANAGED_TABLE_CONFIGS, type ManagedTableConfig } from '@oneup/shared';

export interface ManagementPageConfig {
  title: string;
  description: string;
  source?: ManagementSource;
}

interface AirtableSourceArgs {
  label: string;
  tableId?: string;
}

type SetSearchParams = (
  nextInit: URLSearchParams | ((prev: URLSearchParams) => URLSearchParams),
  navigateOptions?: { replace?: boolean },
) => void;

const MANAGEMENT_VIEW_STORAGE_VERSION = 'v1';

const AIRTABLE_TABLE_IDS = {
  tasks: 'tblFdhdlyFkwT7Kqy',
  organizations: 'tblwBFd0qgmpF0u5i',
  contacts: 'tblZQRlLWxkG3Be6b',
  websites: 'tblmzNvC9OMAnHX1F',
  proposals: 'tbl8qv0FjeDxipVJb',
  leads: 'tblAT63loEUpjTyBG',
} as const;

export const MANAGEMENT_PAGES = {
  tasks: {
    title: 'Tasks',
    description: 'Daily operating queue for active work, review, billing readiness, and delivery.',
    source: airtableSource({ label: 'ENT_Projects', tableId: AIRTABLE_TABLE_IDS.tasks }),
  },
  proposals: {
    title: 'Proposals',
    description: 'Proposal workflow for drafts, client-visible offers, and pricing review.',
    source: airtableSource({ label: 'BILL_Proposals', tableId: AIRTABLE_TABLE_IDS.proposals }),
  },
  leads: {
    title: 'Leads',
    description: 'Prospect intake and opportunity review for sales pipeline work.',
    source: airtableSource({ label: 'Leads', tableId: AIRTABLE_TABLE_IDS.leads }),
  },
  organizations: {
    title: 'Organizations',
    description: 'Client and partner organization records, status, support load, and billing context.',
    source: airtableSource({
      label: 'ENT_Organizations',
      tableId: AIRTABLE_TABLE_IDS.organizations,
    }),
  },
  contacts: {
    title: 'Contacts',
    description: 'Client contacts, user roles, and relationship context.',
    source: airtableSource({ label: 'ENT_Users', tableId: AIRTABLE_TABLE_IDS.contacts }),
  },
  websites: {
    title: 'Websites',
    description: 'Website inventory, hosting status, ownership, and recurring hosting details.',
    source: airtableSource({ label: 'ENT_Websites', tableId: AIRTABLE_TABLE_IDS.websites }),
  },
  forms: managedTablePage(MANAGED_TABLE_CONFIGS.forms),
  formEntries: managedTablePage(MANAGED_TABLE_CONFIGS.formEntries),
  timeEntries: managedTablePage(MANAGED_TABLE_CONFIGS.timeEntries),
  invoices: managedTablePage(MANAGED_TABLE_CONFIGS.invoices),
  subscriptions: managedTablePage(MANAGED_TABLE_CONFIGS.subscriptions),
  payments: managedTablePage(MANAGED_TABLE_CONFIGS.payments),
  agents: managedTablePage(MANAGED_TABLE_CONFIGS.agents),
  calls: managedTablePage(MANAGED_TABLE_CONFIGS.calls),
  prompts: managedTablePage(MANAGED_TABLE_CONFIGS.prompts),
} satisfies Record<string, ManagementPageConfig>;

export type ManagementPageKey = keyof typeof MANAGEMENT_PAGES;

export function parseManagementView(
  value: string | null,
  fallback: ManagementViewMode = 'table',
): ManagementViewMode {
  if (value === 'cards') return 'cards';
  if (value === 'table') return 'table';
  return fallback;
}

export function usePersistentManagementView(
  pageKey: string,
  searchParams: URLSearchParams,
  setSearchParams: SetSearchParams,
  defaultView: ManagementViewMode = 'table',
): [ManagementViewMode, (mode: ManagementViewMode) => void] {
  const queryView = searchParams.get('view');
  const storageKey = managementViewStorageKey(pageKey);
  const [storedView, setStoredView] = useState<ManagementViewMode>(() =>
    readStoredManagementView(storageKey, defaultView),
  );
  const viewMode = queryView ? parseManagementView(queryView, defaultView) : storedView;

  useEffect(() => {
    if (!queryView) return;
    const nextView = parseManagementView(queryView, defaultView);
    setStoredView(nextView);
    writeStoredManagementView(storageKey, nextView);
  }, [defaultView, queryView, storageKey]);

  useEffect(() => {
    if (queryView) return;
    setStoredView(readStoredManagementView(storageKey, defaultView));
  }, [defaultView, queryView, storageKey]);

  const setViewMode = useCallback(
    (mode: ManagementViewMode) => {
      setStoredView(mode);
      writeStoredManagementView(storageKey, mode);
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (mode === 'table') params.delete('view');
          else params.set('view', mode);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams, storageKey],
  );

  return [viewMode, setViewMode];
}

export function airtableSource({ label, tableId }: AirtableSourceArgs): ManagementSource {
  return {
    label,
    system: 'Airtable',
    href: buildAirtableHref(tableId),
  };
}

function managedTablePage(config: ManagedTableConfig): ManagementPageConfig {
  return {
    title: config.title,
    description: config.description,
    source: airtableSource({ label: config.tableName, tableId: config.tableId }),
  };
}

function buildAirtableHref(tableId: string | undefined): string | undefined {
  const baseUrl = import.meta.env.VITE_ONEUP_AIRTABLE_BASE_URL;
  if (!baseUrl || !tableId) return undefined;
  return `${baseUrl.replace(/\/$/, '')}/${tableId}`;
}

function managementViewStorageKey(pageKey: string) {
  return `oneup:management-view:${MANAGEMENT_VIEW_STORAGE_VERSION}:${pageKey}`;
}

function readStoredManagementView(
  storageKey: string,
  fallback: ManagementViewMode,
): ManagementViewMode {
  try {
    return parseManagementView(window.localStorage.getItem(storageKey), fallback);
  } catch {
    return fallback;
  }
}

function writeStoredManagementView(storageKey: string, mode: ManagementViewMode) {
  try {
    window.localStorage.setItem(storageKey, mode);
  } catch {
    // localStorage can be unavailable in restricted browsing modes.
  }
}
